import { CollectionHelper, CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { QueryColumnSortOrder } from '@barthauer/abc/core/data';
import { convertDataType, GridColumnDataType } from '@barthauer/abc/ui-components';
import { DataType } from 'devextreme/common';
import { CustomQueryColumn } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import {
  ColumnObject,
  Commands,
  CRUDPermissions,
  FormatSettings,
  LabelLayoutSettings,
  LayoutColumn,
  LayoutColumnType,
  LayoutDocument,
  LayoutGroup,
  LayoutGroupType,
  LayoutSettings,
  MinimizedLayoutColumn,
  RefListSettings,
  SpecialType,
  SubLayoutReference,
  SubLayoutView,
  TableLayoutSettings,
} from '../models/layout.model';

export class LayoutViewModel {
  private _columns: LayoutColumnViewModel[];
  private _groups: LayoutGroupViewModel[];
  private _subLayoutReferences: SubLayoutReferenceViewModel[];
  private _subLayoutViews: SubLayoutViewModel[];
  private _allColumns: LayoutColumnViewModel[];
  private _isRefColumns: LayoutColumnViewModel[];
  private _commands: Commands[];
  private mapperIndex = 0;
  private mapperGridOnFormIndex = 0;
  private _columnsObjectMap: Map<string, ColumnObject[]> = new Map();
  private _complexOpathColumnsObjectMap: Map<string, ColumnObject[]> = new Map();
  private _gridOnFormColumnObjectMap: Map<MinimizedLayoutColumn, ColumnObject> = new Map();
  private _gridOnFormColumnObjectsMap: Map<string, Map<MinimizedLayoutColumn, ColumnObject>> = new Map();
  private _denyInsert: boolean;
  private _denyUpdate: boolean;
  private _denyDelete: boolean;
  private _images: string[];
  private _hasDocumentsEditorInLayout: boolean = false;
  public get columnsObjectMap(): Map<string, ColumnObject[]> {
    return this._columnsObjectMap;
  }

  public get gridOnFormColumnObjectMap() {
    return this._gridOnFormColumnObjectsMap;
  }

  public get complexOpathColumnsObjectMap(): Map<string, ColumnObject[]> {
    return this._complexOpathColumnsObjectMap;
  }

  public pushToColumnMapper(key: MinimizedLayoutColumn, columnObject: ColumnObject) {
    columnObject.Key = 'F' + this.mapperIndex++;
    if (columnObject.LayoutColumn) {
      columnObject.LayoutColumn.ParentKey = columnObject.Key;
    }
    if (columnObject.LayoutColumn?.SpecialType === SpecialType.DocumentsEditor) {
      this._hasDocumentsEditorInLayout = true;
    }
    if (this._columnsObjectMap.has(JSON.stringify(key))) {
      this._columnsObjectMap.get(JSON.stringify(key)).push(columnObject);
    } else {
      this._columnsObjectMap.set(JSON.stringify(key), [columnObject]);
    }
    this.setComplexOpathColumn(key, columnObject);
  }

  public pushToGridOnFormColumnMapper(key: MinimizedLayoutColumn, columnObject: ColumnObject, columnsIndex: number) {
    if (columnObject.LayoutColumn) {
      columnObject.Key = 'GOF_' + columnsIndex + this.mapperGridOnFormIndex++;
      columnObject.LayoutColumn.ParentKey = columnObject.Key;
    }
    this._gridOnFormColumnObjectMap.set(key, columnObject);
  }

  private setComplexOpathColumn(key: MinimizedLayoutColumn, column: ColumnObject): void {
    if (
      column.LayoutColumn?.OPath?.search(/\[|]|!/gm) > -1 &&
      !column.LayoutColumn?.OPath?.startsWith('Comments[', 0) &&
      !column.LayoutColumn?.OPath?.startsWith('Properties[', 0)
    ) {
      if (this._complexOpathColumnsObjectMap.has(JSON.stringify(key))) {
        this._complexOpathColumnsObjectMap.get(JSON.stringify(key)).push(column);
      } else {
        this._complexOpathColumnsObjectMap.set(JSON.stringify(key), [column]);
      }
    }
  }

  get images(): string[] {
    return this._images;
  }
  set images(value: string[]) {
    this._images = value;
  }

  get caption(): string {
    return this.layoutObject.Caption;
  }

  set caption(value: string) {
    this.layoutObject.Caption = value;
  }

  get objectType(): string {
    return this.layoutObject.ObjectType;
  }
  get oPath(): string {
    return this.layoutObject.OPath;
  }
  private _currentCRUDPermissions: CRUDPermissions;
  get currentCRUDPermissions(): CRUDPermissions {
    return this._currentCRUDPermissions;
  }
  set currentCRUDPermissions(value: CRUDPermissions) {
    this._currentCRUDPermissions = value;
  }
  get denyInsert(): boolean {
    if (this.currentCRUDPermissions?.DenyInsert) {
      return true;
    } else if (!CompareHelper.isNullorEmpty(this._denyInsert)) {
      return this._denyInsert;
    } else if (this.layoutObject.DenyInsert) {
      return this.layoutObject.DenyInsert;
    }
  }
  set denyInsert(val: boolean) {
    this._denyInsert = val;
  }
  get denyUpdate(): boolean {
    if (this.currentCRUDPermissions?.DenyUpdate) {
      return true;
    } else if (!CompareHelper.isNullorEmpty(this._denyUpdate)) {
      return this._denyUpdate;
    } else if (this.layoutObject.DenyUpdate) {
      return this.layoutObject.DenyUpdate;
    }
  }
  set denyUpdate(val: boolean) {
    this._denyUpdate = val;
  }
  get denyDelete(): boolean {
    if (this.currentCRUDPermissions?.DenyDelete) {
      return true;
    } else if (!CompareHelper.isNullorEmpty(this._denyDelete)) {
      return this._denyDelete;
    } else if (this.layoutObject.DenyDelete) {
      return this.layoutObject.DenyDelete;
    }
  }
  set denyDelete(val: boolean) {
    this._denyDelete = val;
  }
  get isReadOnly(): boolean {
    if (this.currentCRUDPermissions?.IsReadOnly) {
      return true;
    } else if (this.layoutObject.IsReadOnly) {
      return this.layoutObject.IsReadOnly;
    }
  }

  set isReadOnly(value: boolean) {
    this.layoutObject.IsReadOnly = value;
  }

  private _mainGroup: LayoutGroupViewModel;
  private get mainGroup(): LayoutGroupViewModel {
    if (!this._mainGroup && this.layoutObject.MainGroup) {
      this._mainGroup = new LayoutGroupViewModel(this, this.layoutObject.MainGroup);
    }
    return this._mainGroup;
  }

  get groups(): LayoutGroupViewModel[] {
    if (!this._groups) {
      this._groups = this.mainGroup?.groups ?? [];
    }
    return this._groups;
  }

  get columns(): LayoutColumnViewModel[] {
    if (!this._columns) {
      this._columns = this.mainGroup?.columns ?? [];
    }
    return this._columns;
  }

  get commands(): Commands[] {
    const standardCommands = ['Documents', 'Properties', 'Comments'];
    if (!this._commands) {
      this._commands = [];
      if (this.layoutObject.Commands) {
        for (const command of this.layoutObject.Commands) {
          if (standardCommands.indexOf(command.Name) === -1) {
            this._commands.push(command);
          }
        }
      }
    }
    return this._commands;
  }

  get subLayoutReferences(): SubLayoutReferenceViewModel[] {
    if (!this._subLayoutReferences) {
      this._subLayoutReferences = [];
      if (this.layoutObject.SubLayoutReferences) {
        for (let subLayoutReference of this.layoutObject.SubLayoutReferences) {
          this._subLayoutReferences.push(new SubLayoutReferenceViewModel(subLayoutReference));
        }
      }
    }
    return this._subLayoutReferences;
  }

  get subLayoutViews(): SubLayoutViewModel[] {
    if (!this._subLayoutViews) {
      this._subLayoutViews = [];
      if (this.layoutObject.SubLayoutViews) {
        for (let subLayoutView of this.layoutObject.SubLayoutViews) {
          this._subLayoutViews.push(new SubLayoutViewModel(subLayoutView));
        }
      }
    }
    return this._subLayoutViews;
  }

  get gridColumns(): LayoutColumnViewModel[] {
    const groupedGridColumns = CollectionHelper.groupBy(
      (this.allColumns ?? []).filter((_) => _.layoutColumnObject?.LayoutColumn?.GridColumnSettings),
      (_) => _.oPath + '|' + (_.layoutColumnObject?.LayoutColumn?.GridColumnSettings?.Caption ?? _.caption),
    );
    return groupedGridColumns.map((_) => _[0]);
  }

  get allColumns(): LayoutColumnViewModel[] {
    if (CompareHelper.isEmptyObject(this._allColumns) && !CompareHelper.isEmptyObject(this.columns)) {
      this._allColumns = [];
      this._isRefColumns = [];

      for (let index = 0; index < this.columns.length; index++) {
        const column: LayoutColumnViewModel = this.columns[index];
        this._allColumns.push(column);
        if (column.isRef) {
          this._isRefColumns[column.dataField] = column;
        }
      }

      if (this.groups?.length) {
        for (let index = 0; index < this.groups.length; index++) {
          const group = this.groups[index];
          this.setSubLayoutViewsAndColumnsInsideGroup(group, index);
          if (group.groups?.length) {
            for (let j = 0; j < this.groups.length; j++) {
              const subGroup = group.groups[j];
              this.setSubLayoutViewsAndColumnsInsideGroup(subGroup, j);
            }
          }
        }
      }
    }
    return this._allColumns;
  }

  private setSubLayoutViewsAndColumnsInsideGroup(group: LayoutGroupViewModel, index: number) {
    const cols = group?.columns ? group.columns : [];
    const subLayoutViews = group?.subLayoutViews ? group.subLayoutViews : [];
    for (let i = 0; i < cols?.length; i++) {
      const column = cols[i];
      this._allColumns.push(column);

      if (column.type !== 'GroupCaption') {
        if (column.isRef) {
          this._isRefColumns[column.dataField] = column;
        }
      }
    }
    for (let j = 0; j < subLayoutViews?.length; j++) {
      const columns = subLayoutViews[j].columns;
      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];
        this.pushToGridOnFormColumnMapper(col.minimizedLayoutColumn, col.layoutColumnObject, j);

        if (columns[i].isRef) {
          this._isRefColumns[col.dataField] = col;
        }
      }
      this._gridOnFormColumnObjectsMap.set(index + '_' + j, this._gridOnFormColumnObjectMap);
      // this._gridOnFormColumnObjectsMap[index] = this._gridOnFormColumnObjectMap;
      this._gridOnFormColumnObjectMap = new Map();
    }
  }

  public resetVisibleGridColumns() {
    for (const c of this.gridColumns) {
      c.updateVisibility();
    }
  }

  get visibleGridColumns(): LayoutColumnViewModel[] {
    const columns = this.gridColumns.filter((_) => _.visible && _.visibleIndex >= 0);
    if (!columns.find((_) => _.dataField === 'Id')) {
      columns.push(
        new LayoutColumnViewModel({
          Key: 'Id',
          LayoutColumn: new LayoutColumn({
            Caption: 'Id',
            OPath: 'Id',
            DataType: GridColumnDataType.String,
            GridColumnSettings: {
              Caption: 'Id',
            },
          }),
        }),
      );
    }
    if (!columns.find((_) => _.dataField === 'ObjectType')) {
      columns.push(
        new LayoutColumnViewModel({
          Key: 'ObjectType',
          LayoutColumn: new LayoutColumn({
            Caption: 'ObjectType',
            OPath: 'ObjectType',
            DataType: GridColumnDataType.Integer32,
            GridColumnSettings: {
              Caption: 'ObjectType',
            },
          }),
        }),
      );
    }
    return columns;
  }

  get mainGroupTableLayoutSettings(): TableLayoutSettings {
    return this.layoutObject.MainGroup?.TableLayoutSettings;
  }

  get isRefColumns(): LayoutColumnViewModel[] {
    return this._isRefColumns;
  }

  constructor(
    public readonly id: string,
    public readonly layoutObject: LayoutDocument,
  ) {
    this.layoutObject = new LayoutDocument(layoutObject);
  }

  public ensureAllColumnsLoaded() {
    return this.allColumns;
  }
  public updateColumnVisibility(dataField: string, visibleIndex: number, visible: boolean) {
    const c = this.gridColumns.find((_) => _.dataField === dataField);
    if (c) {
      c.visible = visible;
      c.visibleIndex = visibleIndex;
    }
  }

  public refreshCustomColumn(customColumn: CustomQueryColumn): void {
    if (customColumn?.name && customColumn?.opath) {
      let c = this.allColumns.find((_) => _.dataField === customColumn.name);
      if (!c?.isUserDefined) {
        c = new LayoutColumnViewModel({
          LayoutColumn: new LayoutColumn({
            ParentKey: customColumn.name,
            Caption: customColumn.caption,
            OPath: customColumn.opath,
            DataType: customColumn.dataType == 'number' ? GridColumnDataType.Double : GridColumnDataType.String,
            IsVisible: 'true',
            GridColumnSettings: {
              Caption: customColumn.caption,
              VisibleIndex: customColumn.visibleIndex,
            },
          }),
        });
        c.isUserDefined = true;
        this.allColumns.push(c);
      }
      c.caption = customColumn.caption;
      c.oPath = customColumn.opath;
      c.gridCaption = customColumn.caption;
      if (c.layoutColumnObject?.LayoutColumn?.GridColumnSettings) {
        c.layoutColumnObject.LayoutColumn.GridColumnSettings.Caption = customColumn.caption;
      }
    }
  }

  public removeCustomColumn(columnHeaderName: string): void {
    const columnIndex: number = this.allColumns.findIndex((column) => column.dataField === columnHeaderName);
    this.allColumns.splice(columnIndex, 1);
  }

  public hasDocumentsEditorInLayout(): boolean {
    return this._hasDocumentsEditorInLayout;
  }
}

export class LayoutGroupViewModel {
  private _columns: LayoutColumnViewModel[];
  private _groups: LayoutGroupViewModel[];
  private _subLayoutViews: SubLayoutViewModel[];
  get caption(): string {
    return this.layoutGroupObject?.Caption;
  }
  get subLayoutViews(): SubLayoutViewModel[] {
    if (!this._subLayoutViews) {
      this._subLayoutViews = [];
      if (this.layoutGroupObject.SubLayoutViews) {
        for (let subLayoutView of this.layoutGroupObject.SubLayoutViews) {
          this._subLayoutViews.push(new SubLayoutViewModel(subLayoutView));
        }
      }
    }
    return this._subLayoutViews;
  }
  get type(): LayoutGroupType {
    return this.layoutGroupObject?.Type;
  }
  get tableLayoutSettings(): TableLayoutSettings {
    return this.layoutGroupObject?.TableLayoutSettings;
  }
  get layoutSettings(): LayoutSettings {
    return this.layoutGroupObject?.LayoutSettings;
  }
  get groups(): LayoutGroupViewModel[] {
    if (!this._groups) {
      this._groups = [];
      if (this.layoutGroupObject?.Groups) {
        for (const g of this.layoutGroupObject?.Groups) {
          this._groups.push(new LayoutGroupViewModel(this.layoutViewModel, g));
        }
      }
    }
    return this._groups;
  }
  get columns(): LayoutColumnViewModel[] {
    if (!this._columns) {
      this._columns = [];
      const cols = this.layoutGroupObject?.Columns;
      if (cols) {
        for (let index = 0; index < cols.length; index++) {
          const col = new LayoutColumnViewModel({ LayoutColumn: cols[index] });
          if ((!col.gridCaption || col.gridCaption === 'UNKNOWN') && cols.length === 1) {
            col.gridCaption = this.caption;
          }
          this.layoutViewModel.pushToColumnMapper(col.minimizedLayoutColumn, col.layoutColumnObject);
          this._columns.push(col);
        }
      }
    }
    return this._columns;
  }
  constructor(
    private layoutViewModel: LayoutViewModel,
    private layoutGroupObject: LayoutGroup,
  ) {}
}

export class SubLayoutViewModel {
  opath: string;
  caption: string;
  layoutSettings?: LayoutSettings;
  private _columns: LayoutColumnViewModel[];
  private _specialTypeOpath: string;
  private _defaultLayoutReferenceId: string;
  private _layoutReferenceId: string;

  get columns(): LayoutColumnViewModel[] {
    if (!this._columns) {
      this._columns = [];
      const cols = this.subLayoutViewObject?.Columns;
      for (let index = 0; index < cols?.length; index++) {
        const col = cols[index];
        this._columns.push(new LayoutColumnViewModel({ LayoutColumn: col }));
      }
    }
    return this._columns;
  }
  get specialTypeOpath(): string {
    if (!this._specialTypeOpath) {
      for (let index = 0; index < this.columns.length; index++) {
        if (this.columns[index]?.specialType) {
          this._specialTypeOpath = this.columns[index].oPath;
        }
      }
    }
    return this._specialTypeOpath;
  }

  get defaultLayoutReferenceId(): string {
    if (!this._defaultLayoutReferenceId) {
      for (let index = 0; index < this.columns.length; index++) {
        if (this.columns[index]?.specialType === SpecialType.DefaultLayoutReferenceId) {
          this._defaultLayoutReferenceId = this.columns[index].oPath;
        }
      }
    }
    return this._defaultLayoutReferenceId;
  }

  get layoutReferenceId(): string {
    if (!this._layoutReferenceId) {
      for (let index = 0; index < this.columns.length; index++) {
        if (this.columns[index]?.specialType === SpecialType.LayoutReferenceId) {
          this._layoutReferenceId = this.columns[index].oPath;
        }
      }
    }
    return this._layoutReferenceId;
  }
  constructor(private subLayoutViewObject: SubLayoutView) {
    this.opath = subLayoutViewObject.OPath;
    this.caption = subLayoutViewObject.Caption;
    this.layoutSettings = subLayoutViewObject?.LayoutSettings;
  }
}

export class SubLayoutReferenceViewModel {
  id: string;
  targetLayout: string;
  targetLayoutOPath: string;
  targetLayoutCaption: string;
  constructor(subLayoutReference: SubLayoutReference) {
    this.id = subLayoutReference.Id;
    this.targetLayout = subLayoutReference.TargetLayout;
    this.targetLayoutOPath = subLayoutReference.TargetLayoutOPath;
    this.targetLayoutCaption = subLayoutReference.TargetCaption;
  }
}

export class LayoutColumnViewModel {
  caption: string;
  gridCaption?: string;
  get dataField(): string {
    return this.layoutColumnObject.LayoutColumn.ParentKey ?? this.layoutColumnObject.Key;
  }
  get isEditableComment(): boolean {
    return this.layoutColumnObject?.LayoutColumn?.isEditableComment;
  }
  dataType: DataType;
  readonly editMask?: string;
  formatSettings?: FormatSettings;
  readonly format?: unknown;
  isReadOnly?: boolean;
  readonly color?: string;
  readonly backColor?: string;
  readonly fontBold?: boolean;
  readonly isRefTextEditable?: boolean;
  readonly isRef?: boolean;
  isHiddenInDataForm?: boolean;
  isUserDefined?: boolean;
  visible?: boolean;
  readonly labelLayoutSettings?: LabelLayoutSettings;
  readonly layoutSettings?: LayoutSettings;
  readonly minimizedLayoutColumn?: MinimizedLayoutColumn;
  oPath: string;
  readonly refListSettings?: RefListSettings;
  readonly sortIndex?: number;
  readonly sortOrder?: 'asc' | 'desc';
  specialType?: SpecialType;
  showInColumnChooser?: boolean;
  readonly hideInColumnChooser?: boolean;
  readonly type?: LayoutColumnType;
  visibleIndex?: number;
  constructor(public readonly layoutColumnObject?: ColumnObject) {
    if (!layoutColumnObject) {
      return;
    }
    for (const key in this.layoutColumnObject.LayoutColumn) {
      if (Object.prototype.hasOwnProperty.call(this.layoutColumnObject.LayoutColumn, key)) {
        const element = this.layoutColumnObject.LayoutColumn[key];
        this[ConvertHelper.camelize(key)] = element;
      }
    }
    if (
      this.layoutColumnObject.LayoutColumn.IsRef &&
      this.layoutColumnObject.LayoutColumn.SpecialType === SpecialType.Property
    ) {
      this.layoutColumnObject.LayoutColumn.IsRef = false;
    }
    this.caption = this.layoutColumnObject.LayoutColumn.Caption?.replace(':', '');
    this.gridCaption = this.layoutColumnObject.LayoutColumn.GridColumnSettings?.Caption || this.caption;
    this.oPath = this.layoutColumnObject.LayoutColumn.IsRef
      ? 'ref:' + this.layoutColumnObject.LayoutColumn.OPath
      : this.layoutColumnObject.LayoutColumn.OPath;
    this.dataType = convertDataType(this.layoutColumnObject.LayoutColumn.DataType)
      ? convertDataType(this.layoutColumnObject.LayoutColumn.DataType)
      : 'string';
    this.sortOrder =
      this.layoutColumnObject.LayoutColumn.GridColumnSettings?.SortOrder ===
      <QueryColumnSortOrder>(<unknown>'Ascending')
        ? 'asc'
        : this.layoutColumnObject.LayoutColumn.GridColumnSettings?.SortOrder ===
            <QueryColumnSortOrder>(<unknown>'Descending')
          ? 'desc'
          : undefined;
    this.sortIndex = <number>this.layoutColumnObject.LayoutColumn.GridColumnSettings?.SortIndex;
    this.editMask = this.layoutColumnObject.LayoutColumn.GridColumnSettings?.FormatSettings?.EditMask;
    this.updateVisibility();
    if (this.layoutColumnObject.LayoutColumn.IsReadOnly === true) {
      this.isReadOnly = true;
    } else if (
      this.layoutColumnObject.LayoutColumn.IsReadOnly === false ||
      this.layoutColumnObject.LayoutColumn.IsEditable === true
    ) {
      this.isReadOnly = false;
    }
    if (this.layoutColumnObject.LayoutColumn.IsRef) {
      this.isRef = true;
      if (this.layoutColumnObject.LayoutColumn.IsEditable === true) {
        this.isRefTextEditable = true;
      }
    }

    if (!layoutColumnObject.LayoutColumn.GridColumnSettings) {
      if (
        layoutColumnObject.LayoutColumn.OPath?.startsWith('Properties[') ||
        layoutColumnObject.LayoutColumn.OPath?.startsWith('Comments[')
      ) {
        this.layoutColumnObject.LayoutColumn.GridColumnSettings = {
          Caption: this.gridCaption,
          VisibleIndex: -1,
        };
        this.hideInColumnChooser = true;
        this.updateVisibility();
      }
    }

    if (!this.backColor && this.isReadOnly) {
      this.backColor = '#0000000b';
    }
    if (!this.color && this.isReadOnly) {
      this.color = '#00000090';
    }
  }

  public updateVisibility(): void {
    this.visibleIndex =
      <number>this.layoutColumnObject.LayoutColumn?.GridColumnSettings?.VisibleIndex?.valueOf() >= 0
        ? <number>this.layoutColumnObject.LayoutColumn?.GridColumnSettings?.VisibleIndex
        : -1;
    this.showInColumnChooser =
      this.layoutColumnObject?.Key === 'Id' || this.layoutColumnObject?.Key === 'ObjectType' || this.hideInColumnChooser
        ? false
        : true;
    this.visible = this.layoutColumnObject.LayoutColumn?.IsHidden || this.visibleIndex == -1 ? false : true;
    this.isHiddenInDataForm = this.layoutColumnObject.LayoutColumn?.IsHidden;
  }
}
